import { useEffect, useId, useMemo, useState } from 'react';

import type { ProjectDto, TaskDto } from '@time-tracking/shared';

/** The pinned General activities entry. Omit it to hide the pin. */
export interface GeneralActivitiesPin {
  /**
   * Names of the active lead-assigned activities, in the grid's order; null
   * while unknown (loading or failed). The count badge and the sub-line both
   * read it.
   */
  activityNames: readonly string[] | null;
  active: boolean;
  onSelect: () => void;
}

interface ProjectTreeProps {
  projects: ProjectDto[];
  tasks: TaskDto[];
  selectedProjectId: string | null;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  currentUserId: string;
  isAdmin: boolean;
  /** Only approvers/admins can create projects; leads can view but not create. */
  canCreate: boolean;
  onSelect: (projectId: string) => void;
  onNewProject: () => void;
  /** Approvers and administrators only; a non-approver project lead gets no pin. */
  generalActivities?: GeneralActivitiesPin;
}

/**
 * Alphabetical by name, case-insensitive and digit-aware so "Phase 2" sorts
 * before "Phase 10". Copies first: `sort` mutates, and `projects` is cache data.
 */
function sortProjects(projects: readonly ProjectDto[]): ProjectDto[] {
  return [...projects].sort((a, b) => {
    const byName = a.name.localeCompare(b.name, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    // `sensitivity: 'base'` calls names that differ only in case or accent
    // equal, and the projects query has no ORDER BY of its own — so without a
    // tie-break those rows swap places between refetches. Code is unique.
    return byName !== 0 ? byName : a.code.localeCompare(b.code);
  });
}

/**
 * The first project as the sidebar lists it — the Projects & tasks landing
 * when General activities is not the default. Archived projects are listed
 * last, behind a collapsed disclosure, so they are never the landing.
 */
export function firstSidebarProjectId(
  projects: readonly ProjectDto[],
  currentUserId: string,
  isAdmin: boolean,
): string | null {
  const active = sortProjects(projects).filter((p) => p.archivedAt === null);
  const leads = (p: ProjectDto) => p.leads.some((l) => l.userId === currentUserId);
  const first = isAdmin ? active[0] : (active.find(leads) ?? active[0]);
  return first?.projectId ?? null;
}

function ListChecksIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 7 5 9 9 5" />
      <polyline points="3 17 5 19 9 15" />
      <line x1="13" y1="6" x2="21" y2="6" />
      <line x1="13" y1="12" x2="21" y2="12" />
      <line x1="13" y1="18" x2="21" y2="18" />
    </svg>
  );
}

/**
 * The pin's sub-line, from the live lead-assigned activities rather than a
 * hardcoded list: the handoff's "Who gets Communications, CSOL Operations and
 * other shared time" names the first two and sums up the rest. With none known
 * (loading, failed, or none active) it names no activity.
 */
export function generalActivitiesPinSubtitle(names: readonly string[] | null): string {
  const [first, second, ...rest] = names ?? [];
  if (first === undefined) return 'Who gets each kind of shared time';
  if (second === undefined) return `Who gets ${first}`;
  return rest.length === 0
    ? `Who gets ${first} and ${second}`
    : `Who gets ${first}, ${second} and other shared time`;
}

function GeneralActivitiesPinButton({ pin }: { pin: GeneralActivitiesPin }) {
  const subId = useId();
  const count = pin.activityNames?.length ?? null;
  const label =
    count === null
      ? 'General activities'
      : `General activities, ${count} ${count === 1 ? 'activity' : 'activities'}`;
  return (
    <div className="ga-pin-wrap">
      <button
        type="button"
        className={`ga-pin${pin.active ? ' ga-pin--active' : ''}`}
        onClick={pin.onSelect}
        aria-current={pin.active ? 'true' : undefined}
        aria-label={label}
        aria-describedby={subId}
      >
        <span className="ga-pin__icon" aria-hidden="true">
          <ListChecksIcon />
        </span>
        <span className="ga-pin__text">
          <span className="ga-pin__title">General activities</span>
          <span className="ga-pin__sub" id={subId}>
            {generalActivitiesPinSubtitle(pin.activityNames)}
          </span>
        </span>
        {count !== null && (
          <span className="ga-pin__count" aria-hidden="true">
            {count}
          </span>
        )}
      </button>
    </div>
  );
}

function FolderIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 13 13"
      fill="currentColor"
      aria-hidden="true"
      style={{ color: active ? 'var(--ut-burnt-orange)' : 'var(--fg3)', flexShrink: 0 }}
    >
      <path d="M1 4a1 1 0 0 1 1-1h2.5l1 1.5H11a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V4z" />
    </svg>
  );
}

function Chevron({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0, color: 'var(--fg3)' }}
    >
      {expanded ? <path d="M2 3.5l3 3 3-3" /> : <path d="M3.5 2l3 3-3 3" />}
    </svg>
  );
}

function ProjectItem({
  project,
  tasks,
  isSelected,
  isExpanded,
  onSelect,
}: {
  project: ProjectDto;
  tasks: TaskDto[];
  isSelected: boolean;
  isExpanded: boolean;
  onSelect: () => void;
}) {
  const taskCount = project.taskCount;

  // The row is a disclosure when the region below it has anything in it: the
  // project's teams, its task list, or both. Teams ride on the projects query so
  // they are known for every row; the task list is only loaded for the selected
  // one, where `tasks` is what is actually rendered. Tying the state to that
  // rather than to taskCount keeps the row from announcing "expanded" while the
  // tasks query is still in flight, or after the last task is deleted while
  // taskCount is stale.
  const hasTeams = project.teams.length > 0;
  const hasTaskList = isSelected ? tasks.length > 0 : taskCount > 0;
  const hasDisclosure = hasTeams || hasTaskList;

  return (
    <>
      <button
        type="button"
        className={`projects-sidebar__item${isSelected ? ' projects-sidebar__item--active' : ''}`}
        onClick={onSelect}
        // Selection drives the detail pane and is styled with a background and a
        // burnt-orange rule. Since collapsing no longer clears it, aria-expanded
        // can't stand in for it any more, and colour must not be the only cue.
        aria-current={isSelected ? 'true' : undefined}
        aria-expanded={hasDisclosure ? isExpanded : undefined}
        // Teams are no longer listed here: they live in the disclosed region
        // below, announced under their own label. Naming them on the row too
        // would read them out for every collapsed project — the audio version of
        // exactly the clutter this arrangement removes — and twice when open.
        // The Archived group heading is a visual grouping; a row reached
        // directly by Tab carries no group context, so the state is named on the
        // row itself as well.
        aria-label={`${project.name} (${project.code}), ${taskCount} task${taskCount !== 1 ? 's' : ''}${
          project.archivedAt ? ', archived' : ''
        }`}
      >
        <div className="projects-sidebar__item-row">
          <Chevron expanded={hasDisclosure && isExpanded} />
          <FolderIcon active={isSelected} />
          <span className="projects-sidebar__item-name">{project.name}</span>
        </div>
        <div className="projects-sidebar__item-meta">
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{project.code}</span>
          <span aria-hidden="true">·</span>
          <span>
            {taskCount} task{taskCount !== 1 ? 's' : ''}
          </span>
          {project.archivedAt && (
            <span className="projects-sidebar__item-archived" aria-hidden="true">
              Archived
            </span>
          )}
          {project.lockedAt && (
            <svg
              width="11"
              height="11"
              viewBox="0 0 12 12"
              fill="currentColor"
              aria-label="Locked"
              role="img"
            >
              <path d="M9 5H8V3.5a2 2 0 0 0-4 0V5H3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1zm-3 3.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM7 5H5V3.5a1 1 0 0 1 2 0V5z" />
            </svg>
          )}
        </div>
      </button>

      {isExpanded && hasTeams && (
        <ul
          role="list"
          aria-label={`Teams on ${project.name}`}
          className="projects-sidebar__team-list"
        >
          {project.teams.map((t) => (
            <li key={t.teamId} className="projects-sidebar__team-pill">
              <span
                aria-hidden="true"
                className="projects-sidebar__team-dot"
                style={{ background: t.color }}
              />
              {t.name}
            </li>
          ))}
        </ul>
      )}

      {isExpanded && tasks.length > 0 && (
        <ul
          role="list"
          aria-label={`Tasks in ${project.name}`}
          className="projects-sidebar__task-list"
        >
          {tasks.map((task) => (
            <li key={task.taskId} className="projects-sidebar__task-item">
              <span className="projects-sidebar__task-name">{task.name}</span>
              {(task.teamIds?.length ?? 0) > 0 && (
                <span className="projects-sidebar__task-meta">
                  {task.teamIds.length} team{task.teamIds.length !== 1 ? 's' : ''}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export function ProjectTree({
  projects,
  tasks,
  selectedProjectId,
  searchQuery,
  onSearchChange,
  currentUserId,
  isAdmin,
  canCreate,
  onSelect,
  onNewProject,
  generalActivities,
}: ProjectTreeProps) {
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const matches = q
      ? projects.filter((p) => p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q))
      : projects;
    return sortProjects(matches);
  }, [projects, searchQuery]);

  const leadsProject = (p: ProjectDto) => p.leads.some((l) => l.userId === currentUserId);
  // Archived projects come out first, so they never appear in My/Shared as well
  // as in their own group. `GET /v1/projects` returns them deliberately — this
  // partition, not a server filter, is what keeps Restore reachable.
  const activeProjects = filtered.filter((p) => p.archivedAt === null);
  const archivedProjects = filtered.filter((p) => p.archivedAt !== null);
  const myProjects = activeProjects.filter(leadsProject);
  const sharedProjects = activeProjects.filter((p) => !leadsProject(p));

  // A real disclosure, not a static label like My/Shared: it holds content, so
  // it needs a keyboard-operable widget with honest `aria-expanded`. Collapsed by
  // default — archived projects are out of circulation and should not push the
  // live ones off the first screen.
  const archivedGroupId = useId();
  const [archivedOpen, setArchivedOpen] = useState(false);
  const selectedIsArchived = archivedProjects.some((p) => p.projectId === selectedProjectId);
  // Archiving from the detail pane moves the project into this group. Leaving it
  // shut would show a detail pane for a project with nothing selected in the
  // sidebar, so opening it on that transition keeps the two halves agreeing.
  useEffect(() => {
    if (selectedIsArchived) setArchivedOpen(true);
  }, [selectedIsArchived]);

  // A search that matches an archived project has to reveal it. `filtered` already
  // narrows this group, so without this the count beside "Archived" ticks up while
  // the row itself stays hidden behind a collapsed disclosure — from the searcher's
  // side the search simply looks broken.
  //
  // Deliberately an effect rather than folding the condition into the rendered
  // expanded state: forcing the group open would leave the toggle reporting
  // `aria-expanded="true"` while clicking it did nothing visible. This opens the
  // group and then gets out of the way, so the user can still fold it shut
  // mid-search. Like the selection effect above it never auto-closes.
  const searchMatchesArchived = searchQuery.trim().length > 0 && archivedProjects.length > 0;
  useEffect(() => {
    if (searchMatchesArchived) setArchivedOpen(true);
  }, [searchMatchesArchived]);

  // Expansion normally tracks selection; this holds the one project the user has
  // explicitly folded shut. Collapsing keeps the project selected, so the detail
  // pane stays put. Selecting any other project clears this, so it expands.
  const [collapsedProjectId, setCollapsedProjectId] = useState<string | null>(null);

  function handleItemClick(project: ProjectDto) {
    const { projectId } = project;
    // Only arm a collapse when the region below the row actually has something
    // in it to fold away — the project's teams, or a loaded task list. A second
    // click on a project with neither (or one whose tasks are still loading)
    // would otherwise set a collapse nothing reveals, silently suppressing the
    // content once it arrives.
    const hasDisclosedContent = project.teams.length > 0 || tasks.length > 0;
    const canCollapse = projectId === selectedProjectId && hasDisclosedContent;
    if (canCollapse && projectId !== collapsedProjectId) {
      setCollapsedProjectId(projectId);
      return;
    }
    setCollapsedProjectId(null);
    onSelect(projectId);
  }

  const renderItem = (p: ProjectDto) => (
    <ProjectItem
      key={p.projectId}
      project={p}
      tasks={p.projectId === selectedProjectId ? tasks : []}
      isSelected={p.projectId === selectedProjectId}
      isExpanded={p.projectId === selectedProjectId && p.projectId !== collapsedProjectId}
      onSelect={() => handleItemClick(p)}
    />
  );

  return (
    <div className="projects-sidebar">
      <div className="projects-sidebar__header">
        <div className="projects-sidebar__search-wrap">
          <svg
            className="projects-sidebar__search-icon"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="6" cy="6" r="4" />
            <path d="M9.5 9.5l3 3" />
          </svg>
          <input
            type="search"
            className="projects-sidebar__search"
            placeholder="Search projects…"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search projects"
          />
        </div>
      </div>

      {canCreate && (
        <div className="projects-sidebar__new-project-row">
          <button
            type="button"
            className="btn btn--primary btn--sm projects-sidebar__new-btn-full"
            onClick={onNewProject}
          >
            + New project
          </button>
          <p className="projects-sidebar__new-hint">
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="currentColor"
              aria-hidden="true"
              style={{ flexShrink: 0, marginTop: 1 }}
            >
              <circle cx="6" cy="6" r="5.5" stroke="currentColor" strokeWidth="1" fill="none" />
              <path d="M6 5.5v3M6 4a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1z" strokeWidth="0" />
            </svg>
            You'll be set as the project lead automatically.
          </p>
        </div>
      )}

      {generalActivities && <GeneralActivitiesPinButton pin={generalActivities} />}

      <nav className="projects-sidebar__list" aria-label="Project list">
        {filtered.length === 0 && (
          <p className="projects-sidebar__empty">
            {searchQuery ? 'No projects match your search.' : 'No projects yet.'}
          </p>
        )}

        {isAdmin ? (
          activeProjects.map(renderItem)
        ) : (
          <>
            {myProjects.length > 0 && (
              <>
                <div className="projects-sidebar__group-label" aria-hidden="true">
                  My projects ({myProjects.length})
                </div>
                {myProjects.map(renderItem)}
              </>
            )}
            {sharedProjects.length > 0 && (
              <>
                <div className="projects-sidebar__group-label" aria-hidden="true">
                  Shared with me ({sharedProjects.length})
                </div>
                {sharedProjects.map(renderItem)}
              </>
            )}
          </>
        )}

        {archivedProjects.length > 0 && (
          <>
            <button
              type="button"
              className="projects-sidebar__group-toggle"
              aria-expanded={archivedOpen}
              aria-controls={archivedGroupId}
              onClick={() => setArchivedOpen((open) => !open)}
            >
              <Chevron expanded={archivedOpen} />
              Archived ({archivedProjects.length})
            </button>
            <div id={archivedGroupId} hidden={!archivedOpen}>
              {archivedProjects.map(renderItem)}
            </div>
          </>
        )}
      </nav>
    </div>
  );
}
